import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { useMain } from '../hooks/useMain';
import TextInputs from 'src/components/inputs/TextInputs';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { MdDelete } from 'react-icons/md';
import { FaRegEdit } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { updateCouponData, resetCouponData, setLoading } from 'src/redux/slices/couponSlice';
import { getHighlightedText } from 'src/utils/textUtils';

const discount_type = ['%', 'INR'];

const Coupons = ({ data, getCoupons }) => {
  const couponGridTemplate = '130px 110px 110px 120px 110px 110px 120px 140px';
  const couponGridRowStyle = {
    display: 'grid',
    gridTemplateColumns: couponGridTemplate,
    alignItems: 'center',
    columnGap: '10px',
  };
  const dispatch = useDispatch();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDialogue, setOpenDialogue] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);

  // Get data from Redux store
  const { initialCouponData, loading , filters} = useSelector((state) => state.coupons);
  const { deleteCoupon, updateCoupon, setShowMessage, activeBranchId } = useMain();

  // Keep all your original handlers with Redux integration
  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateCouponData({ [name]: value }));
  };

  const handleUpdate = (item) => {
    setSelectedItemId(item._id);

    // Destructure _id and use only the rest of the properties
    const { _id, ...updateData } = item;

    dispatch(updateCouponData(updateData)); // Only send update-able fields
    setOpenDialogue(true);
  };

  const handleCloseDialogue = () => {
    setOpenDialogue(false);
    dispatch(resetCouponData());
  };

  const handleUpdateSubmit = async () => {
    try {
      dispatch(setLoading(true));
      const res = await updateCoupon(
        selectedItemId,
        initialCouponData,
        `?branchId=${activeBranchId}`,
      );

      if (res?.statusCode === 200) {
        setShowMessage({
          message: res.message || 'Coupon Details updated successfully',
          messageType: 'success',
        });
        getCoupons();
      }
    } catch (error) {
      setShowMessage({ message: error.message || 'Something went wrong', messageType: 'error' });
    } finally {
      dispatch(setLoading(false));
      handleCloseDialogue();
    }
  };

  const handleDeleteField = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleDiscountTypeChange = (event, newDiscountType) => {
    if (newDiscountType !== null) {
      dispatch(updateCouponData({ discountType: newDiscountType }));
    }
  };

  const confirmDelete = async () => {
    try {
      dispatch(setLoading(true));
      const res = await deleteCoupon(deleteId, `?branchId=${activeBranchId}`);

      if (res?.statusCode === 200) {
        setShowMessage({
          message: res.message || 'Coupon deleted successfully',
          messageType: 'success',
        });
        getCoupons();
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Something went wrong',
        messageType: 'error',
      });
    } finally {
      setOpenDeleteDialog(false);
      setDeleteId(null);
      dispatch(setLoading(false));
    }
  };

  // Preserved your exact UI markup
  return (
    <Box sx={{ overflow: 'auto', width: { xs: '450px', sm: 'auto' } }}>
     <table className="table-container mt-3" style={{ width: '100%' }}>
        <thead className="table-thead">
          <tr style={couponGridRowStyle}>
            {[
              'Coupon',
              'Discount',
              'Min Amount',
              'Max Discount',
              'Validity',
              'Coupon/User',
              'Reward Points',
              'Actions',
            ].map((h, index) => (
              <th key={index} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="table-tbody">
          {data.map((coupon) => {
            const originalDate = coupon?.validTill;
            let formattedValidDate = '';
            if (originalDate) {
              const [year, month, day] = originalDate.split('-');
              formattedValidDate = `${day}-${month}-${year}`;
            }
           return (
              <tr key={coupon._id} style={couponGridRowStyle}>
                <td style={{ textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getHighlightedText(coupon?.couponCode, filters?.search)}</td>                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {coupon?.discount} {coupon?.discountType}
                </td>
                <td style={{ textAlign: 'center' }}>{coupon?.minimumBillAmount}</td>
                <td style={{ textAlign: 'center' }}>{coupon?.maximumDiscountAmount}</td>
                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{formattedValidDate}</td>
                <td style={{ textAlign: 'center' }}>{coupon?.couponPerUser}</td>
                <td style={{ textAlign: 'center' }}>{coupon?.rewardPoints}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    <IconButton
                      onClick={() => handleUpdate(coupon)}
                      color="secondary"
                      title="Update"
                    >
                      <FaRegEdit style={{ color: '#696969', fontSize: '1rem' }} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteField(coupon?._id)}
                      color="error"
                      title="Delete"
                    >
                      <MdDelete color="#FF4D4D" style={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Delete Dialog - Preserved exact UI */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogContent>Are you sure you want to delete this coupon?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog - Preserved exact UI markup */}
      <Dialog open={openDialogue} onClose={handleCloseDialogue} maxWidth="md">
        <DialogTitle>Coupon Details</DialogTitle>
        <DialogContent>
          <DialogContentText>Here you can update Coupon details.</DialogContentText>
          <Box style={{ marginTop: '20px' }}>
            <Grid container spacing={3}>
              <Grid item xs={6} md={4}>
                <TextInputs
                  title={'Coupon Code'}
                  name={'couponCode'}
                  value={initialCouponData?.couponCode || ''}
                  handleChange={handleChange}
                  type={'text'}
                  required={false}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextInputs
                  title={'Discount'}
                  name={'discount'}
                  value={initialCouponData?.discount || ''}
                  handleChange={handleChange}
                  type={'number'}
                  required={false}
                />
              </Grid>
              <Grid item xs={6} md={4} marginTop="0.1rem">
                <div style={{ marginBottom: '3px', fontWeight: 'bold', color: 'black' }}>
                  Discount Type
                </div>
                <ToggleButtonGroup
                  value={initialCouponData?.discountType || ''}
                  exclusive
                  onChange={handleDiscountTypeChange}
                  aria-label="discount type"
                  fullWidth
                  sx={{
                    borderRadius: '4px',
                    height: '50px',
                    backgroundColor: '#f0f0f0',
                    marginTop: '0.1rem',
                  }}
                >
                  {discount_type.map((type) => (
                    <ToggleButton
                      key={type}
                      value={type}
                      aria-label={type}
                      sx={{
                        color: '#000',
                        backgroundColor: '#e0e0e0',
                        height: '3.3rem',
                        '&.Mui-selected': {
                          backgroundColor: '#6174DD',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#6174DD',
                          },
                        },
                        '&:not(.Mui-selected)': {
                          '&:hover': {
                            backgroundColor: '#d0d0d0',
                          },
                        },
                      }}
                    >
                      {type}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </Box>

          {/* Rest of your original dialog content */}
          <Box style={{ marginTop: '20px' }}>
            <Grid container spacing={3}>
              <Grid item xs={6} md={4}>
                <TextInputs
                  title={'Minimum Bill Amount'}
                  name={'minimumBillAmount'}
                  value={initialCouponData?.minimumBillAmount || ''}
                  handleChange={handleChange}
                  type={'number'}
                  required={false}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextInputs
                  title={'Max Discount in Rs.'}
                  name={'maximumDiscountAmount'}
                  value={initialCouponData?.maximumDiscountAmount || ''}
                  handleChange={handleChange}
                  type={'number'}
                  required={false}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextInputs
                  title={'Coupon Per User'}
                  name={'couponPerUser'}
                  value={initialCouponData?.couponPerUser || ''}
                  handleChange={handleChange}
                  type={'number'}
                  required={false}
                />
              </Grid>
            </Grid>
          </Box>

          <Box style={{ marginTop: '20px' }}>
            <Grid container spacing={3}>
              <Grid item xs={6} md={4}>
                <TextInputs
                  title={'Valid Till'}
                  name={'validTill'}
                  value={initialCouponData?.validTill || ''}
                  handleChange={handleChange}
                  type={'date'}
                  min={new Date().toISOString().split('T')[0]}
                  required={false}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextInputs
                  title={'Reward Points'}
                  name={'rewardPoints'}
                  value={initialCouponData?.rewardPoints || ''}
                  handleChange={handleChange}
                  type={'number'}
                  required={false}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          {loading ? (
            <ShowLoader />
          ) : (
            <>
              <div style={{ width: '200px', margin: '3rem auto' }}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  sx={{ background: '#6174DD' }}
                  onClick={handleCloseDialogue}
                >
                  Cancel
                </Button>
              </div>
              <div style={{ width: '200px', margin: '3rem auto' }}>
                <Button
                  variant="contained"
                  fullWidth
                  color="primary"
                  sx={{ background: '#6174DD' }}
                  onClick={handleUpdateSubmit}
                >
                  Update
                </Button>
              </div>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Coupons;